<template>
  <div
    class="panel is-info mb-3"
    :class="{ 'is-collapsed': !isOpen }"
    :data-test-id="testId"
    :data-section-key="sectionKey"
  >
    <p
      class="panel-heading is-size-6 py-2"
      style="cursor: pointer"
      role="button"
      tabindex="0"
      :aria-expanded="isOpen"
      data-test-id="collapsible-header"
      @click="toggle"
      @keydown.enter="toggle"
      @keydown.space.prevent="toggle"
    >
      <span class="icon-text">
        <slot name="title">
          <span>{{ title }}</span>
        </slot>
        <span v-if="badge" class="tag is-info is-light ml-2">{{ badge }}</span>
      </span>
      <span class="icon is-pulled-right chevron-icon" aria-hidden="true">
        <i class="fas fa-angle-down"></i>
      </span>
    </p>
    <SlideTransition :disabled="skipTransition">
      <div
        v-if="isOpen"
        class="is-block collapsible-panel-block"
        :class="{ 'panel-block': !noPanelBlock }"
      >
        <slot></slot>
      </div>
    </SlideTransition>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
  import SlideTransition from "./slide-transition.vue";

  const props = defineProps({
    title: { type: String, default: "" },
    badge: { type: [String, Number], default: null },
    defaultOpen: { type: Boolean, default: true },
    sectionKey: { type: String, default: null },
    testId: { type: String, default: null },
    noPanelBlock: { type: Boolean, default: false },
  });

  const isOpen = ref(props.defaultOpen);
  const skipTransition = ref(true);
  const TOUCH_MAX_WIDTH = 1023; // match Bulma's touch breakpoint
  const isTouchViewport = ref(false);
  let touchMediaQuery = null;
  let touchChangeHandler = null;

  onMounted(() => {
    // Force open on touch/mobile viewports where headers are hidden
    if (typeof window !== "undefined" && window.matchMedia) {
      touchMediaQuery = window.matchMedia(`(max-width: ${TOUCH_MAX_WIDTH}px)`);
      touchChangeHandler = (mq) => {
        isTouchViewport.value = mq.matches;
      };
      touchChangeHandler(touchMediaQuery);
      touchMediaQuery.addEventListener("change", touchChangeHandler);

      if (touchMediaQuery.matches) {
        isOpen.value = true;
      }
    }

    window.addEventListener("supplement:open-section", handleOpenSectionRequest);

    nextTick(() => {
      skipTransition.value = false;
    });
  });

  onBeforeUnmount(() => {
    if (touchMediaQuery && touchChangeHandler) {
      touchMediaQuery.removeEventListener("change", touchChangeHandler);
    }
    window.removeEventListener("supplement:open-section", handleOpenSectionRequest);
  });

  function handleOpenSectionRequest(event) {
    if (event.detail?.key === props.sectionKey && !isTouchViewport.value) {
      open({ autoFocusFirstField: true });
      nextTick(() => {
        const el = document.querySelector(`[data-section-key="${props.sectionKey}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  }

  const emit = defineEmits(["toggle"]);

  function toggle() {
    if (isTouchViewport.value) return;

    isOpen.value = !isOpen.value;
    emit("toggle", isOpen.value, { autoFocusFirstField: false });
  }

  function open(options = {}) {
    if (!isOpen.value) {
      isOpen.value = true;
      emit("toggle", true, options);
    }
  }

  defineExpose({ open, isOpen });
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_editor";

  .panel {
    scroll-margin-top: 0.5rem;
  }

  .panel-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  @include chevron-rotation(".panel", ".panel-heading");
</style>
